import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { routes } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { AbsenceFinanceReportService } from '../../services/absence-finance-report.service';
import { TimeOffService } from "@app/modules/time-off/services/time-off.service";
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import moment from 'moment';
import { from } from 'rxjs';
import { CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-absence-finance-details-data-grid',
    templateUrl: './absence-finance-report-details-data-grid.component.html',
    styleUrls: ['./absence-finance-report-details-data-grid.component.scss']
})
export class AbsenceFinanceReportDetailsDataGridComponent implements OnInit {
    @ViewChild('excelexport') excelexport: any;
    public grid: GridComponent;
    public routes: typeof routes = routes;
    public bindingType = 'array';
    public gridDataResult: GridDataResult;
    excelData: any;
    gridId: string = 'AbsenceFinanceReportDetailsDataGrid';
    isLoading: boolean;
    pageSize: number = 20;
    skip: number = 0;

    sortString: string;
    filterString: string;
    filterToggleDetails: {
        text: string,
        checked: boolean
    } = {
        text: 'IgnoreFutureTimeOff',
        checked: true
    }

    excelColumns: any[] = [
        { field: 'employee', title: this.translate.instant('AbsenceFinance-Employee') },
        { field: 'timeOffType', title: this.translate.instant('AbsenceFinance-TimeOffType') },
        { field: 'totalHoursAvailable', title: this.translate.instant('AbsenceFinance-TotalHoursAvailable') },
        { field: 'salary', title: this.translate.instant('AbsenceFinance-Salary') },
        { field: 'hourlyRate', title: this.translate.instant('AbsenceFinance-HourlyRate') },
        { field: 'quarterlyTotal', title: this.translate.instant('AbsenceFinance-Outstanding-Liability') },
    ];


    public columns: any[] = [
        { field: 'employee', title: this.translate.instant('AbsenceFinance-Employee'), localizationCode: 'AbsenceFinance-Employee', type: 'employee' },
        { field: 'timeOffType', subField: 'name', title: this.translate.instant('AbsenceFinance-TimeOffType'), localizationCode: 'AbsenceFinance-TimeOffType' },
        { field: 'totalHoursAvailable', title: this.translate.instant('AbsenceFinance-TotalHoursAvailable'), dataType: 'String', localizationCode: 'AbsenceFinance-TotalHoursAvailable' },
        { field: 'salary', title: this.translate.instant('AbsenceFinance-Salary'), dataType: 'String', localizationCode: 'AbsenceFinance-Salary' },
        { field: 'hourlyRate', title: this.translate.instant('AbsenceFinance-HourlyRate'), dataType: 'String', localizationCode: 'AbsenceFinance-HourlyRate' },
        { field: 'quarterlyTotal', title: this.translate.instant('AbsenceFinance-Outstanding-Liability'), dataType: 'String', localizationCode: 'AbsenceFinance-Outstanding-Liability' },
    ];

    sortableColumns: any[] = [
        { field: 'employee', sortValue: ['employee.firstName', 'employee.lastName'] },
        { field: 'timeOffType', sortValue: 'timeOffType.name' },
        { field: 'totalHoursAvailable', sortValue: 'totalHoursAvailable' },
        { field: 'salary', sortValue: 'salary' },
        { field: 'hourlyRate', sortValue: 'hourlyRate' },
        { field: 'quarterlyTotal', sortValue: 'quarterlyTotal' },
    ];

    filterCategories: any[] = [
        { field: "employee.firstname", title: this.translate.instant('EmployeeFirstname'), type: "String", dataType: 'String' },
        { field: "employee.lastname", title: this.translate.instant('EmployeeLastname'), type: "String", dataType: 'String' },
        { field: "timeOffType.id", title: this.translate.instant('AbsenceFinance-TimeOffType'), dataType: "SpecialLookup", lookupCode: 'TIME_OFF_TYPE' },
        { field: "totalHoursAvailable", title: this.translate.instant('AbsenceFinance-TotalHoursAvailable'), type: "String", dataType: 'Number' },
        { field: "salary", title: this.translate.instant('AbsenceFinance-Salary'), type: "String", dataType: 'Number' },
        { field: "hourlyRate", title: this.translate.instant('AbsenceFinance-HourlyRate'), type: "String", dataType: 'Number' },
        { field: "quarterlyTotal", title: this.translate.instant('AbsenceFinance-Outstanding-Liability'), type: "String", dataType: 'Number' },
        { field: "employee.status.id", title: this.translate.instant('Employee-Status'), dataType: "ProvidedList", listOptions: {
            list: [
                {id: 'Active', name: this.translate.instant('Employee-Status-Active')}, 
                {id: 'InActive', name: this.translate.instant('Employee-Status-InActive')},
                {id: 'Future', name: this.translate.instant('Employee-Status-Future')},
            ],
            id: 'id',
            name: 'name'
        }},
    ];

    searchValue: any;
    searchFilterString: string;
    getRequest: any;
    error: boolean;
    showAsOfDateField: boolean = true;
    asOfDate: any = moment().format('YYYY-MM-DD');
    fileName: string = this.translate.instant('AbsenceFinanceReport') + '_' + moment().format('YYYY-MM-DD') + '.xlsx';

    constructor(
        private translate: TranslateService,
        private overlayService: OverlayService,
        private currencyPipe: CurrencyPipe,
        private absenceFinanceReportService: AbsenceFinanceReportService
    ) {
        this.getData();

        // Bind 'this' explicitly to capture the execution context of the component.
        this.allData = this.allData.bind(this);
    }

    ngOnInit(): void {
    }

    public allData() {
        this.overlayService.show('Loading');

        from(this.absenceFinanceReportService.getAllReportData(this.sortString, this.filterString, this.asOfDate, this.filterToggleDetails.checked))
            .subscribe({
                next: (res) => {
                    this.excelData = res.data;

                    this.excelData.map((item) => {
                        item.employee = `${item.employee.firstName} ${item.employee.lastName}`;
                        item.timeOffType = item.timeOffType?.name;

                        if(item.currency?.id){
                            item.salary = this.currencyPipe.transform( item.salary, item.currency.id, 'symbol', '1.2-2');
                            item.quarterlyTotal = this.currencyPipe.transform( item.quarterlyTotal, item.currency.id, 'symbol', '1.2-2');
                        }
                    });

                    // if I dont have this timeout first time I download the report the data will be blank
                    setTimeout(() => {
                        this.excelexport.save();
                    }, 1000);
                },
                error: (e) => {
                    this.overlayService.hide();
                },
                complete: () => this.overlayService.hide()
            });
    }

    getData(): void {
        this.isLoading = true;
        this.getRequest = this.absenceFinanceReportService.getReportData(this.pageSize, this.skip, this.sortString, this.filterString, this.asOfDate, this.filterToggleDetails.checked)
            .subscribe(
                {
                    next: (v) => {
                        this.gridDataResult = {
                            data: v.data,
                            total: v.total,
                        }

                        this.gridDataResult.data.map((item) => {
                            if(item.currency?.id){
                                item.salary = this.currencyPipe.transform( item.salary, item.currency.id, 'symbol', '1.2-2');
                                item.quarterlyTotal = this.currencyPipe.transform( item.quarterlyTotal, item.currency.id, 'symbol', '1.2-2');
                            }
                        });
                    },
                    error: (e) => {
                        this.error = true;
                        this.isLoading = false;
                    },
                    complete: () => this.isLoading = false
                }
            );
    }

    gridStateChange(gridState: GridState) {
        if (this.pageSize !== gridState.pageSize) {
            this.pageSize = gridState.pageSize;
        }
        if (this.skip !== gridState.skip) {
            this.skip = gridState.skip;
        }
        if (this.sortString !== gridState.sortString) {
            this.sortString = gridState.sortString;
        }
        if (this.filterString !== gridState.filter) {
            this.filterString = gridState.filter;
            this.skip = 0;
        }
        if (this.asOfDate !== gridState.asOfDate) {
            this.asOfDate = gridState.asOfDate;
        }
        if (this.filterToggleDetails.checked !== gridState.filterToggleDetails?.checked) {
            this.filterToggleDetails.checked = gridState.filterToggleDetails?.checked;
        }

        this.getRequest.unsubscribe();
        this.getData();
    }
}
